import { Search } from "lucide-react";
import { useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useGroups, useMachines } from "../api/queries";
import type { ProtectionStatus } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { protectionSeverity } from "../lib/status";
import { ErrorState, LoadingRows, OsTag, PageHead } from "../ui/Common";
import { ProtectionPill } from "../ui/Pills";
import { RestoreStrip } from "../ui/RestoreStrip";
import { machinesMessages } from "./machines.i18n";

const FILTERS: (ProtectionStatus | "all")[] = ["all", "failed", "stale", "never", "protected", "revoked"];

export function MachinesPage() {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();
  const t = useT(machinesMessages);
  const tc = useT(common);
  const f = useFormat();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as ProtectionStatus | null) ?? undefined;
  const groupId = params.get("grupo") ?? undefined;
  const q = params.get("q") ?? "";

  const all = useMachines(orgId);
  const groups = useGroups(orgId);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (all.data ?? [])
      .filter((m) => (!status || m.status === status) && (!groupId || m.groupId === groupId) && (!needle || m.hostname.toLowerCase().includes(needle)))
      .sort((a, b) => protectionSeverity[a.status] - protectionSeverity[b.status] || a.hostname.localeCompare(b.hostname));
  }, [all.data, status, groupId, q]);

  function set(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const count = (s: ProtectionStatus | "all") => (s === "all" ? all.data?.length ?? 0 : all.data?.filter((m) => m.status === s).length ?? 0);

  return (
    <>
      <PageHead
        title={t.title}
        lede={t.lede}
        actions={<Link className="btn btn-primary" to={`/o/${orgId}/add-machines`}>{t.addMachines}</Link>}
      />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div className="segmented" role="group" aria-label={t.statusFilter}>
          {FILTERS.map((s) => (
            <button key={s} type="button" aria-pressed={(s === "all" && !status) || s === status} onClick={() => set("status", s === "all" ? undefined : s)}>
              {s === "all" ? t.all : tc.protection[s]} <span className="n">{count(s)}</span>
            </button>
          ))}
        </div>
        {(groups.data?.length ?? 0) > 0 && <select className="select" style={{ width: 220 }} value={groupId ?? ""} onChange={(e) => set("grupo", e.target.value || undefined)} aria-label={t.groupFilter} id="filtro-grupo">
          <option value="">{t.allGroups}</option>
          {groups.data?.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.machineCount})</option>)}
        </select>}
        <div className="search" style={{ flex: "1 1 220px", maxWidth: 320 }}>
          <Search aria-hidden="true" />
          <input className="input" type="search" placeholder={t.search} value={q} onChange={(e) => set("q", e.target.value || undefined)} aria-label={t.search} id="busca-hostname" />
        </div>
      </div>

      <section className="panel">
        {all.isPending ? <LoadingRows rows={10} /> : all.isError ? <div className="panel-body"><ErrorState error={all.error} /></div> : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{t.colMachine}</th>
                  <th>{t.colStatus}</th>
                  <th>{t.colLast30}</th>
                  <th>{t.colLastBackup}</th>
                  <th className="right">{t.colProtected}</th>
                  <th>{t.colAgent}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const href = `/o/${orgId}/machines/${encodeURIComponent(m.id)}`;
                  return (
                    <tr key={m.id} className="clickable" onClick={() => navigate(href)}>
                      <td>
                        <div className="host">
                          <Link to={href} onClick={(e) => e.stopPropagation()}><strong>{m.hostname}</strong></Link>
                          <span>{m.osVersion}</span>
                        </div>
                      </td>
                      <td><ProtectionPill status={m.status} /></td>
                      <td><RestoreStrip days={m.restorePoints} size="mini" /></td>
                      <td className="num">{m.lastSuccessAt ? f.ago(m.lastSuccessAt) : <span className="muted">{t.never}</span>}</td>
                      <td className="right num">{m.protectedBytes ? f.bytes(m.protectedBytes) : <span className="muted">—</span>}</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span className={`dot ${m.online ? "on" : "off"}`} aria-hidden="true" />
                          <span className="ink-2">{m.online ? t.online : t.seen(f.ago(m.lastSeenAt))}</span>
                          <OsTag os={m.os} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={6}><div className="empty">{t.empty}</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
